/**
 * Servicio API para background jobs
 *
 * Endpoints core:
 *   POST api/jobs/reconciliation           — encolar reconciliación
 *   POST api/jobs/contract-assignment      — encolar asignación de contratos
 *   GET  api/jobs/{id}                     — poll job (core)
 *   POST api/jobs/{id}/restart             — reintentar job fallido
 *
 * Endpoints finance:
 *   POST api/finance/eligible-invoices-report/jobs        — encolar reporte
 *   GET  api/finance/eligible-invoices-report/jobs/{id}   — poll job (finance)
 *   GET  api/finance/eligible-invoices-report/jobs/{id}/download — descargar resultado
 */
import { appConfig } from "@/config/app-config";
import type { BackgroundJobDto, EnqueueResponse, HistoryJobDto, JobStatus, JobType } from "@/types/jobs";
import { JobConflictError } from "@/types/jobs";

// Mapeo de jobType numérico (API .NET) a string (FE types)
const JOB_TYPE_MAP: Record<number | string, JobType> = {
  0: "BookingReconciliation",
  1: "ContractAssignment",
  2: "EligibleInvoicesReport",
  4: "CurrencyRateSync",
  "BookingReconciliation": "BookingReconciliation",
  "ContractAssignment": "ContractAssignment",
  "EligibleInvoicesReport": "EligibleInvoicesReport",
  "CurrencyRateSync": "CurrencyRateSync",
};

function normalizeJobType(jobType: unknown): JobType {
  const mapped = JOB_TYPE_MAP[jobType as keyof typeof JOB_TYPE_MAP];
  return mapped || ("EligibleInvoicesReport" as JobType); // fallback seguro
}

// ═══════════════════════════════════════════════════════════════════════════
// Auth helpers (igual que reconciliation-api.ts)
// ═══════════════════════════════════════════════════════════════════════════

function getAuthToken(): string | null {
  try {
    const oidcKey = `oidc.user:${appConfig.oidc.authority}:${appConfig.oidc.clientId}`;
    const oidcData = sessionStorage.getItem(oidcKey);
    if (oidcData) {
      const parsed = JSON.parse(oidcData);
      return parsed.access_token || null;
    }
  } catch {
    // silencioso — el caller recibirá 401 del servidor
  }
  return null;
}

function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body?.error ?? body?.title ?? body?.message ?? `Error ${response.status}`;
  } catch {
    return `Error ${response.status}`;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Helper: encolar (POST) con manejo de 409
// ═══════════════════════════════════════════════════════════════════════════

async function enqueueJob(url: string, body: object): Promise<EnqueueResponse> {
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (response.status === 409) {
    let activeJobId: number;
    try {
      const data = await response.json();
      activeJobId = data?.activeJobId ?? data?.jobId ?? 0;
    } catch {
      activeJobId = 0;
    }
    throw new JobConflictError(activeJobId);
  }

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  // API may return 202 with empty body — safe parse
  try {
    const text = await response.text();
    if (text) return JSON.parse(text) as EnqueueResponse;
  } catch {
    // empty body — jobId unknown until poll reveals it
  }
  return { jobId: 0 };
}

// ═══════════════════════════════════════════════════════════════════════════
// Funciones públicas
// ═══════════════════════════════════════════════════════════════════════════

export async function enqueueReconciliation(
  entityId: number,
  bookingIds?: number[],
): Promise<EnqueueResponse> {
  const body: Record<string, unknown> = { entityId };
  if (bookingIds && bookingIds.length > 0) body.bookingIds = bookingIds;
  return enqueueJob(appConfig.getApiUrl("jobs/reconciliation"), body);
}

export interface ContractAssignmentOptions {
  entityId: number;
  bookingId?: number | null;
  bookingIds?: number[];
  operationType?: string | null;
  forceReassign?: boolean;
  includePending?: boolean;
  maxBookings?: number;
}

export async function enqueueContractAssignment(
  options: ContractAssignmentOptions,
): Promise<EnqueueResponse> {
  const { entityId, bookingId, bookingIds, operationType, forceReassign = false, includePending = false, maxBookings = 0 } = options;
  const body: Record<string, unknown> = { entityId, forceReassign, includePending, maxBookings };
  if (bookingIds && bookingIds.length > 0) body.bookingIds = bookingIds;
  else if (bookingId != null) body.bookingId = bookingId;
  if (operationType) body.operationType = operationType;
  return enqueueJob(appConfig.getApiUrl("jobs/contract-assignment"), body);
}

export async function enqueueEligibleInvoicesReport(entityId: number): Promise<EnqueueResponse> {
  return enqueueJob(appConfig.getFinanceUrl("finance/eligible-invoices-report/jobs"), { entityId });
}

export async function pollJob(id: number, service: "core" | "finance"): Promise<BackgroundJobDto> {
  const url =
    service === "core"
      ? appConfig.getApiUrl(`jobs/${id}`)
      : appConfig.getFinanceUrl(`finance/eligible-invoices-report/jobs/${id}`);

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const dto = await response.json();
  return {
    ...dto,
    jobType: normalizeJobType(dto.jobType),
  };
}

export async function restartJob(id: number): Promise<EnqueueResponse> {
  const response = await fetch(appConfig.getApiUrl(`jobs/${id}/restart`), {
    method: "POST",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  // API may return 200 with empty body — safe parse
  try {
    const text = await response.text();
    if (text) return JSON.parse(text) as EnqueueResponse;
  } catch {
    // empty body
  }
  return { jobId: 0 };
}

export interface JobsHistoryPage {
  rows: HistoryJobDto[];
  total: number;
}

export async function fetchJobsHistory(
  page: number,
  pageSize: number,
  statusFilter?: JobStatus[],
  jobTypeFilter?: string[],
): Promise<JobsHistoryPage> {
  const startRow = page * pageSize;
  const endRow = startRow + pageSize;

  const filterGroups: object[][] = [];
  if (statusFilter && statusFilter.length > 0)
    filterGroups.push(statusFilter.map((s) => ({ property: "status", value: s, operator: "Equals" })));
  if (jobTypeFilter && jobTypeFilter.length > 0)
    filterGroups.push(jobTypeFilter.map((t) => ({ property: "jobType", value: t, operator: "Equals" })));

  const response = await fetch(appConfig.getApiUrl("jobs"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      startRow,
      endRow,
      sortProperty: "createdOn",
      sortDirection: "desc",
      ...(filterGroups.length > 0 ? { filterGroups } : {}),
    }),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const result = await response.json();
  const rows: HistoryJobDto[] = (result?.data?.rows ?? []).map((row: any) => ({
    ...row,
    jobType: normalizeJobType(row.jobType),
  }));
  const total: number = result?.data?.lastRow ?? rows.length;
  return { rows, total };
}

export async function downloadJobResult(id: number, service: "core" | "finance"): Promise<Blob> {
  const url =
    service === "core"
      ? appConfig.getApiUrl(`jobs/${id}/download`)
      : appConfig.getFinanceUrl(`finance/eligible-invoices-report/jobs/${id}/download`);

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  return response.blob();
}
